import type { Class } from "@classes-service-types/index.ts";
import type { UseQueryOptions } from "@tanstack/vue-query";
import { useQuery } from "@tanstack/vue-query";
import { computed, type Ref, unref } from "vue";
import { learningServiceRequest } from "@/services/utils";

export type ClassOptionParams = Record<string, unknown>;

export const getQueryKey = (params: ClassOptionParams) => ["getClassOptions", params];

interface Args<TData = Class[]> {
  params: Ref<ClassOptionParams>;
  useQueryOptions?: Partial<UseQueryOptions<Class[], Error, TData>>;
}

/**
 * Class options for the academics selects. Every instance that asks for the same
 * filters shares one query key, so the recursive track configurator resolves to a
 * single request no matter how many selects it renders.
 */
export const useGetClassOptions = <TData = Class[]>({ params, useQueryOptions }: Args<TData>) => {
  const getClassOptions = async (): Promise<Class[]> => {
    const response = await learningServiceRequest({
      method: "get",
      url: "/classes",
      params: { ...unref(params) },
      failureMessage: "Error loading Classes data",
    });

    return response?.data?.data ?? [];
  };

  return useQuery<Class[], Error, TData>({
    queryKey: computed(() => getQueryKey(unref(params))),
    queryFn: getClassOptions,
    ...useQueryOptions,
  });
};
